import type { ReplayBundle, ReplayFix, ReplayMarker, ReplayParticipant } from './replayTypes'

/**
 * Position of a participant at `cursor`, or null when they are not on the map at
 * that instant.
 *
 * "Not on the map" is a real answer, not an edge case: a hunter who joined an
 * hour into the hunt has no position for that hour, and drawing them at their
 * first fix would show someone standing in the woods before they arrived.
 *
 * Between two fixes the position is linearly interpolated. Straight-line
 * interpolation is honest about what the data supports — the archive records
 * where a carrier was, not the path taken between readings.
 */
export function markerAt(participant: ReplayParticipant, cursor: number): ReplayMarker | null {
  if (cursor < participant.windowStart || cursor > participant.windowEnd) return null

  const fixes = participant.fixes
  if (fixes.length === 0) return null

  // Before the first fix the carrier is inside its window but has not reported
  // yet. Showing the first fix here is deliberate: the window says they were
  // present, so the alternative — no marker — would contradict the roster.
  if (cursor <= fixes[0].t) return toMarker(participant, fixes[0])

  const last = fixes[fixes.length - 1]
  if (cursor >= last.t) return toMarker(participant, last)

  const index = upperBound(fixes, cursor)
  const before = fixes[index - 1]
  const after = fixes[index]

  const span = after.t - before.t
  // Unreachable while `fixes` is ascending, which everything upstream guarantees.
  // Kept because the alternative on unsorted input is a division by zero or a
  // negative ratio, i.e. a marker somewhere off the map rather than a stale one.
  if (span <= 0) return toMarker(participant, before)

  const ratio = (cursor - before.t) / span
  return {
    participant,
    lat: before.lat + (after.lat - before.lat) * ratio,
    lon: before.lon + (after.lon - before.lon) * ratio,
    // Heading and speed are instrument readings, not positions: interpolating
    // them would invent measurements the device never took. The reading in force
    // at this moment is the one from the fix already passed.
    heading: before.heading,
    speed: before.speed,
    barking: before.barking === true,
  }
}

function toMarker(participant: ReplayParticipant, fix: ReplayFix): ReplayMarker {
  return {
    participant,
    lat: fix.lat,
    lon: fix.lon,
    heading: fix.heading,
    speed: fix.speed,
    barking: fix.barking === true,
  }
}

/**
 * Index of the first fix strictly after `cursor`. Binary search rather than a
 * scan because this runs once per participant per animation frame, and a long
 * hunt holds thousands of fixes per carrier.
 */
function upperBound(fixes: ReplayFix[], cursor: number): number {
  let low = 0
  let high = fixes.length
  while (low < high) {
    const mid = (low + high) >>> 1
    if (fixes[mid].t <= cursor) low = mid + 1
    else high = mid
  }
  return low
}

/** Markers for every participant on the map at `cursor`, hidden ones excluded. */
export function markersAt(
  bundle: ReplayBundle,
  cursor: number,
  hiddenParticipants: ReadonlySet<string>,
): ReplayMarker[] {
  const markers: ReplayMarker[] = []
  for (const participant of bundle.participants) {
    if (hiddenParticipants.has(participant.id)) continue
    const marker = markerAt(participant, cursor)
    if (marker !== null) markers.push(marker)
  }
  return markers
}

/**
 * The hunt's own extent, widened to cover any fix that falls outside it.
 *
 * Archives do contain fixes a little before the recorded start or after the
 * recorded end (a collar reporting on either side of the hunt being opened and
 * closed). Trusting the header alone would make those fixes unreachable by the
 * scrubber, so the trail would visibly extend past where playback can go.
 */
export function replayExtent(bundle: ReplayBundle): { startsAt: number; endsAt: number } {
  let startsAt = bundle.startsAt
  let endsAt = bundle.endsAt
  for (const participant of bundle.participants) {
    const fixes = participant.fixes
    if (fixes.length === 0) continue
    if (fixes[0].t < startsAt) startsAt = fixes[0].t
    const last = fixes[fixes.length - 1].t
    if (last > endsAt) endsAt = last
  }
  // A hunt with no duration at all would make every progress ratio a division by
  // zero downstream; one second of extent keeps the scrubber well-defined.
  if (endsAt <= startsAt) endsAt = startsAt + 1000
  return { startsAt, endsAt }
}
